import React, { type FC, useState } from 'react';
import { getComposeLink } from '../../api/ListingEmails/listingEmails';
import { getErrorMessages } from '../../utility/team';
import { ComposeLink, isWebMail, ListingEmailStatus, listingEmailDraftUrl } from '../../utility/listingEmail';

// Email Listing Agent: a download link when a draft can be made, otherwise a greyed button and what is missing.
type ListingEmailCellProps = { listSortId: string; propertyId: string; status?: ListingEmailStatus; emailClient?: string };

const ListingEmailCell: FC<ListingEmailCellProps> = ({ listSortId, propertyId, status, emailClient }) => {
  const [ busy, setBusy ] = useState(false);
  const [ problem, setProblem ] = useState<string | null>(null);
  const [ attachNote, setAttachNote ] = useState<string[] | null>(null);

  // Gmail / Outlook on the web: open the compose window and download the documents to attach.
  // The window is opened first (while the click still counts) so the browser does not block it.
  const handleCompose = async () => {
    setBusy(true);
    setProblem(null);
    setAttachNote(null);
    const tab = window.open('about:blank', '_blank');
    try {
      const link = (await getComposeLink(listSortId, propertyId)).data as ComposeLink;
      if (tab) tab.location.href = link.compose_url; else window.location.href = link.compose_url;
      const download = document.createElement('a');
      download.href = link.attachments_url;
      download.download = '';
      document.body.appendChild(download);
      download.click();
      download.remove();
      setAttachNote(link.attachments);
    } catch (err) {
      tab?.close();
      setProblem(getErrorMessages(err)[0]);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <span className='mt-1 block text-xs text-gray-400 print:hidden'>Checking...</span>;
  if (status.ready) {
    return (
      <>
      {isWebMail(emailClient) ? (
        <button
          type='button'
          data-tag='email-listing-agent-button'
          disabled={busy}
          onClick={handleCompose}
          title='Open a compose window with the letter filled in and download the documents to attach'
          className='mt-1 inline-block rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-50 print:hidden'
        >
          {busy ? 'Opening...' : 'Email agent'}
        </button>
      ) : (
        <a
          data-tag='email-listing-agent-button'
          href={listingEmailDraftUrl(listSortId, propertyId)}
          download
          title={status.agent_email ? `Draft an email to ${status.agent_email} with the contract attached` : 'Draft an email with the contract attached'}
          className='mt-1 inline-block rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white hover:bg-indigo-500 print:hidden'
        >
          Email agent
        </a>
      )}
      {attachNote && (
        <p className='mt-1 text-xs text-gray-600 print:hidden' data-tag='email-listing-agent-attach-note'>
          Attach the downloaded files to your email: {attachNote.join(', ')}
        </p>
      )}
      {problem && <p role='alert' className='mt-1 text-xs text-red-600 print:hidden'>{problem}</p>}
      {(status.notes ?? []).length > 0 && (
        <ul className='mt-1 text-xs text-gray-500 print:hidden' data-tag='email-listing-agent-notes'>
          {(status.notes ?? []).map((note) => <li key={note}>{note}</li>)}
        </ul>
      )}
      </>
    );
  }
  return (
    <div className='mt-1 print:hidden' data-tag='email-listing-agent-missing'>
      <button type='button' disabled className='cursor-not-allowed rounded bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-400'>Email agent</button>
      <ul className='mt-1 text-xs text-amber-700'>{status.missing.map((m) => <li key={m}>{m}</li>)}</ul>
    </div>
  );
};

export default ListingEmailCell;
